import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Plus from 'lucide-react/dist/esm/icons/plus.js';
import X from 'lucide-react/dist/esm/icons/x.js';
import Pencil from 'lucide-react/dist/esm/icons/pencil.js';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down.js';
import ChevronUp from 'lucide-react/dist/esm/icons/chevron-up.js';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2.js';
import ArrowLeft from 'lucide-react/dist/esm/icons/arrow-left.js';
import Search from 'lucide-react/dist/esm/icons/search.js';
import Zap from 'lucide-react/dist/esm/icons/zap.js';
import Layers from 'lucide-react/dist/esm/icons/layers.js';
import MessageCircle from 'lucide-react/dist/esm/icons/message-circle.js';
import Clock from 'lucide-react/dist/esm/icons/clock.js';
import Bell from 'lucide-react/dist/esm/icons/bell.js';
import Hourglass from 'lucide-react/dist/esm/icons/hourglass.js';
import Filter from 'lucide-react/dist/esm/icons/filter.js';
import ListChecks from 'lucide-react/dist/esm/icons/list-checks.js';
import { EngagementTabs /* , DotBadge */ } from '../components/engagement/primitives';
import {
  fetchEngagementRules,
  createEngagementRule,
  updateEngagementRule,
  toggleEngagementRule,
  fetchWaTemplateOptions,
  createWaTemplate,
  updateTemplateVariables,
  fetchEngagementRuleCategories,
  fetchEventCatalog,
  createEventCatalogEntry,
  createBookingStep,
} from '../services/engagementService';

// Service options for the "Create New Booking Step" form — same set as
// cep.documents.booking_step.SERVICES. Kept in sync manually since the
// backend enum rarely changes; if it does, the create call still fails
// loudly with a clear validation error rather than silently mismatching.
const BOOKING_STEP_SERVICES = [
  { value: 'vc', label: 'VC Booking Reminder' },
  { value: 'diagnostics', label: 'Diagnostics Booking Reminder' },
  { value: 'loan', label: 'Loan Application Reminder' },
  { value: 'medicine_online', label: 'Medicine Order Reminder (Online)' },
  { value: 'medicine_offline', label: 'Medicine Order Reminder (Offline)' },
  { value: 'credit_line', label: 'CreditLine Application Reminder' },
];

// The service filter (VC/Diagnostics/Loan/Medicine) can't be pushed down to
// the backend as an event_type filter — every Booking Step rule is stored
// with event_type='vc_booking_intent' regardless of which service it's
// actually for (see events/api/views_engagement_rules/create.py), so the
// real service only exists implicitly in the trigger event's name prefix.
// Only login_milestone/cart_abandonment are genuine event_type values the
// backend can filter on; service options filter the already-fetched list.
const SERVICE_FILTERS = [
  { key: 'vc', label: 'VC', prefix: 'vc_' },
  { key: 'diagnostics', label: 'Diagnostics', prefix: 'diag_' },
  { key: 'loan', label: 'Loan', prefix: 'loan_' },
  { key: 'medicine', label: 'Medicine', prefix: 'medicine_' },
  { key: 'credit_line', label: 'CreditLine', prefix: 'credit_line_' },
];
const isServiceFilter = (tab) => SERVICE_FILTERS.some((s) => s.key === tab);

const EVENT_TYPES = [
  { value: 'login_milestone', label: 'Login Milestone' },
  { value: 'vc_funnel_category', label: 'VC Booking Reminder' },
  { value: 'diagnostics_funnel_category', label: 'Diagnostics Booking Reminder' },
  { value: 'loan_funnel_category', label: 'Loan Application Reminder' },
  { value: 'medicine_online_funnel_category', label: 'Medicine Order Reminder (Online)' },
  { value: 'medicine_offline_funnel_category', label: 'Medicine Order Reminder (Offline)' },
  { value: 'credit_line_funnel_category', label: 'CreditLine Application Reminder' },
];

// Maps a formData.event_type value (this dropdown's own local values, NOT a
// backend EventType enum — the backend has no *_funnel_category concept;
// every BookingStep-driven rule always saves with event_type=
// 'vc_booking_intent' regardless of service, see events/api/
// views_engagement_rules/create.py) to the `service` key BookingStep rows
// are tagged with — used to fetch the right subset of Booking Step options.
const EVENT_TYPE_TO_SERVICE = {
  vc_funnel_category: 'vc',
  diagnostics_funnel_category: 'diagnostics',
  loan_funnel_category: 'loan',
  medicine_online_funnel_category: 'medicine_online',
  medicine_offline_funnel_category: 'medicine_offline',
  credit_line_funnel_category: 'credit_line',
};

const SERVICE_TYPES = [
  { value: 'diagnostics', label: 'Diagnostics' },
  { value: 'medicine', label: 'Medicine' },
  { value: 'virtual_consultation', label: 'Virtual Consultation' },
];

// Only 2 options — once_per_day is intentionally not offered here: it's a
// special case of once_per_window (24h), so "every N minutes/hours/days/
// weeks" already covers it with one fewer concept for admins to learn.
// Legacy rules still stored with once_per_day keep working (the backend
// choices tuple still allows it) — the edit form just maps them onto this
// option, see fromWindowHours(24) fallback in handleEdit.
const RATE_LIMIT_STRATEGIES = [
  { value: 'once_ever', label: 'Only once (per subscriber)' },
  { value: 'once_per_window', label: 'Once every N (minutes/hours/days/weeks)' },
];

// EngagementRule.rate_limit_window_hours is a FloatField on the backend
// (min_value=1/60, i.e. "at least 1 minute") — these multipliers convert
// whatever unit the admin picks into hours before it's sent, so the API
// contract stays in hours regardless of which unit the UI shows.
const WINDOW_UNIT_TO_HOURS = {
  minutes: 1 / 60,
  hours: 1,
  days: 24,
  weeks: 24 * 7,
};
const WINDOW_UNITS = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
];
const MIN_WINDOW_HOURS = 1 / 60;
const toWindowHours = (value, unit) => {
  const n = parseFloat(value);
  if (!Number.isFinite(n) || n <= 0) return MIN_WINDOW_HOURS;
  return Math.max(MIN_WINDOW_HOURS, n * (WINDOW_UNIT_TO_HOURS[unit] || 1));
};

// Reverse of toWindowHours, for populating the edit form from a stored
// hours value — picks the largest whole unit the hours count divides
// evenly into, so e.g. 48 shows as "2 days" instead of "48 hours", and
// sub-hour values (0.0167 = 1 minute) show as whole minutes rather than
// an awkward fraction of an hour.
const fromWindowHours = (hours) => {
  const h = Number(hours) || 24;
  const EPS = 1e-6;
  const divides = (unitHours) => Math.abs(Math.round(h / unitHours) - h / unitHours) < EPS;

  if (h < 1 && divides(1 / 60)) return { value: String(Math.round(h * 60)), unit: 'minutes' };
  if (divides(24 * 7) && h >= 24 * 7) return { value: String(Math.round(h / (24 * 7))), unit: 'weeks' };
  if (divides(24) && h >= 24) return { value: String(Math.round(h / 24)), unit: 'days' };
  if (divides(1)) return { value: String(Math.round(h)), unit: 'hours' };
  // Non-whole-hour value with no clean minutes fit either — show as hours
  // with the fraction intact rather than silently rounding.
  return { value: String(h), unit: 'hours' };
};

// Variable sources resolved at send time from the triggering Event itself
// (see cep/services/wa_template.py build_body_params) — event_user is the
// EventUser attached to the event, event_data is that event's own `data` dict.
// `field` for event_data entries is a common key seen across trigger events;
// admins can also type a custom key if their event uses a different one.
const AVAILABLE_FIELDS = [
  { source: 'event_user', field: 'full_name', label: 'Subscriber Full Name' },
  { source: 'event_user', field: 'first_name', label: 'Subscriber First Name' },
  { source: 'event_user', field: 'last_name', label: 'Subscriber Last Name' },
  { source: 'event_user', field: 'mobile_no', label: 'Subscriber Mobile' },
  { source: 'event_user', field: 'email', label: 'Subscriber Email' },
  { source: 'event_user', field: 'ref_code', label: 'Ref Code' },
  { source: 'event_data', field: 'first_name', label: 'Event Data: first_name' },
  { source: 'event_data', field: 'lab_name', label: 'Event Data: lab_name' },
  { source: 'event_data', field: 'service_type', label: 'Event Data: service_type' },
  { source: 'event_data', field: 'sniper_url', label: 'Event Data: sniper_url (pre-authenticated deep link)' },
  { source: 'event_data', field: 'credit_line_id', label: 'Event Data: credit_line_id' },
  { source: 'event_data', field: 'loan_id', label: 'Event Data: loan_id' },
  { source: 'event_data', field: 'lender_code', label: 'Event Data: lender_code' },
];

// Searchable event picker — a plain <input list="..."> (HTML datalist) looks
// like a search box but browsers only fire onChange on an exact, committed
// match, so every keystroke while narrowing down a name reset the field to
// empty. This renders its own dropdown instead: typed text and the
// committed selection are two separate pieces of state, so typing never
// clobbers what's already picked, and a click (or Enter) is what commits it.
const SearchableEventSelect = ({ events, value, onChange, disabled, placeholder }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const selected = events.find((ev) => ev.id === value) || null;

  useEffect(() => {
    // Reflect the committed selection in the box whenever it changes
    // externally (e.g. service switched and value got cleared to '').
    setQuery(selected ? selected.event_name : '');
  }, [selected]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery(selected ? selected.event_name : '');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selected]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return events;
    return events.filter((ev) => ev.event_name.toLowerCase().includes(q));
  }, [events, query]);

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <Search className="w-3 h-3 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <input type="text" disabled={disabled} value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); if (value) onChange(''); }}
          placeholder={placeholder}
          className="w-full border border-gray-300 rounded-md pl-7 pr-3 py-2 text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50" />
      </div>
      {open && !disabled && (
        <div className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
          {filtered.length === 0 ? (
            <div className="px-3.5 py-3 text-sm text-gray-400 italic">No matching events</div>
          ) : (
            filtered.map((ev) => (
              <button key={ev.id} type="button"
                onClick={() => { onChange(ev.id); setQuery(ev.event_name); setOpen(false); }}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 text-gray-700">
                {ev.event_name}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const EngagementRuleConfig = () => {
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [waTemplates, setWaTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [editingVariables, setEditingVariables] = useState([]);
  const [savingVariables, setSavingVariables] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  // Create/edit-rule failures surface INSIDE the form modal (not the
  // page-level error banner) — same reasoning as bookingStepApiError below:
  // the admin is looking at the modal when the operation fails.
  const [formApiError, setFormApiError] = useState('');
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [templateFormData, setTemplateFormData] = useState({ name: '', template_name: '', body: '' });
  const [creatingTemplate, setCreatingTemplate] = useState(false);

  const [showBookingStepForm, setShowBookingStepForm] = useState(false);
  const [eventCatalog, setEventCatalog] = useState([]);
  const [bookingStepFormData, setBookingStepFormData] = useState({
    service: '', label: '', trigger_event: '', target_event: '',
  });
  const [bookingStepErrors, setBookingStepErrors] = useState({});
  const [creatingBookingStep, setCreatingBookingStep] = useState(false);
  // Which dropdown ("trigger_event" | "target_event" | null) currently has
  // its inline "+ Add new event" text input open.
  const [addingEventFor, setAddingEventFor] = useState(null);
  const [newEventName, setNewEventName] = useState('');
  const [creatingEvent, setCreatingEvent] = useState(false);
  // Server-side failures for booking-step / inline-event creation surface
  // INSIDE this modal (not the page-level error banner) — the admin is
  // looking at the modal when they hit "Create"/"Add", so the failure
  // reason should show up right there instead of behind it.
  const [bookingStepApiError, setBookingStepApiError] = useState('');

  const [formData, setFormData] = useState({
    event_type: 'login_milestone',
    service_type: '',
    login_number: '',
    category: '',
    delay_minutes: '5',
    watemplate: '',
    rate_limit_strategy: 'once_ever',
    // rate_limit_window_hours stays the source of truth sent to the backend
    // (EngagementRule.rate_limit_window_hours is an hours-only IntField) —
    // _value/_unit are UI-only convenience state, converted to hours on
    // every change via updateRateLimitWindow() below.
    rate_limit_window_hours: '24',
    rate_limit_window_value: '24',
    rate_limit_window_unit: 'hours',
    // Recurring reminders — Booking Step rules only (backend:
    // EngagementRule.recurring_enabled / max_reminders). Only meaningful
    // when rate_limit_strategy === 'once_per_window'; rate_limit_window_hours
    // doubles as the repeat interval in that case.
    recurring_enabled: false,
    max_reminders: '2',
  });
  const [formVariables, setFormVariables] = useState([]);
  const [formErrors, setFormErrors] = useState({});

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // The 'all' option and service filters both need the full unfiltered
      // list (service filtering happens client-side below); only
      // login_milestone/cart_abandonment map to a real backend event_type filter.
      const eventTypeFilter = (activeTab === 'all' || isServiceFilter(activeTab)) ? null : activeTab;
      const [rulesRes, templatesRes] = await Promise.all([
        fetchEngagementRules(eventTypeFilter),
        fetchWaTemplateOptions(),
      ]);
      if (rulesRes?.metadata?.api_status) {
        setRules(rulesRes.msg || []);
      }
      if (templatesRes?.metadata?.api_status) {
        setWaTemplates(templatesRes.msg || []);
      }
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  const visibleRules = useMemo(() => {
    const filter = SERVICE_FILTERS.find((s) => s.key === activeTab);
    if (!filter) return rules;
    return rules.filter((r) => r.category_event_name?.startsWith(filter.prefix));
  }, [rules, activeTab]);

  // Trigger/Target Event options are scoped to whichever service the
  // booking-step form currently has selected — omit service to clear the
  // list back to empty (there's nothing sensible to show pre-selection).
  const loadEventCatalog = useCallback(async (service) => {
    if (!service) { setEventCatalog([]); return; }
    const res = await fetchEventCatalog(service);
    if (res?.metadata?.api_status) setEventCatalog(res.msg || []);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Booking Step options depend on which service (VC/Diagnostics) is picked —
  // re-fetch the filtered category list whenever that selection changes.
  useEffect(() => {
    const service = EVENT_TYPE_TO_SERVICE[formData.event_type];
    if (!service) { setCategories([]); return; }
    let cancelled = false;
    (async () => {
      const res = await fetchEngagementRuleCategories(service);
      if (!cancelled && res?.metadata?.api_status) setCategories(res.msg || []);
    })();
    return () => { cancelled = true; };
  }, [formData.event_type]);

  const resetForm = () => {
    setFormData({
      event_type: 'login_milestone', service_type: '', login_number: '', category: '',
      delay_minutes: '5', watemplate: '', rate_limit_strategy: 'once_ever',
      rate_limit_window_hours: '24', rate_limit_window_value: '24', rate_limit_window_unit: 'hours',
      recurring_enabled: false, max_reminders: '2',
    });
    setFormVariables([]);
    setFormErrors({});
    setFormApiError('');
    setEditingId(null);
    setShowForm(false);
  };

  // Re-fetch the Booking Step dropdown for whichever service is currently
  // selected — called after a new booking step is created so it shows up
  // immediately without the admin having to re-open the form.
  const refreshBookingStepOptions = async () => {
    const service = EVENT_TYPE_TO_SERVICE[formData.event_type];
    if (!service) return;
    const res = await fetchEngagementRuleCategories(service);
    if (res?.metadata?.api_status) setCategories(res.msg || []);
  };

  const resetBookingStepForm = () => {
    setBookingStepFormData({ service: '', label: '', trigger_event: '', target_event: '' });
    setBookingStepErrors({});
    setBookingStepApiError('');
    setAddingEventFor(null);
    setNewEventName('');
    setShowBookingStepForm(false);
  };

  const openBookingStepForm = () => {
    // Pre-fill the service from whichever funnel is currently selected in
    // the main form, if any — saves a redundant click for the common case.
    const preselectedService = EVENT_TYPE_TO_SERVICE[formData.event_type] || '';
    setBookingStepFormData({ service: preselectedService, label: '', trigger_event: '', target_event: '' });
    setBookingStepErrors({});
    setBookingStepApiError('');
    setShowBookingStepForm(true);
  };

  // DRF validation errors come back as {field: ["message", ...]} — flatten
  // to one readable string. Falls back to the raw msg for plain-string
  // errors (e.g. an unhandled exception's str(e)).
  const flattenApiError = (msg, fallback) => {
    if (!msg) return fallback;
    if (typeof msg === 'string') return msg;
    if (typeof msg === 'object') {
      const flat = Object.values(msg).flat().filter(Boolean).join(' ');
      return flat || fallback;
    }
    return fallback;
  };

  // Trigger/Target Event options are scoped to the booking-step form's own
  // service selection — re-fetch whenever the modal opens or the admin
  // changes that selection (including clearing back to "" on close/reset).
  useEffect(() => {
    if (!showBookingStepForm) return;
    loadEventCatalog(bookingStepFormData.service);
  }, [bookingStepFormData.service, showBookingStepForm, loadEventCatalog]);

  // Changing service invalidates whatever trigger/target event was picked
  // for the previous service — clear them so a stale, now-hidden selection
  // can't silently ride along to submit.
  const handleBookingStepServiceChange = (service) => {
    setBookingStepFormData((prev) => ({ ...prev, service, trigger_event: '', target_event: '' }));
  };

  const validateBookingStepForm = () => {
    const errors = {};
    if (!bookingStepFormData.service) errors.service = 'Service is required';
    if (!bookingStepFormData.label.trim()) errors.label = 'Journey name is required';
    if (!bookingStepFormData.trigger_event) errors.trigger_event = 'Trigger event is required';
    // Target Event is optional — a step without one has no conversion
    // signal to attribute against (see the note under the field), but is
    // still a valid category to hand-pick when creating a rule.
    if (
      bookingStepFormData.trigger_event &&
      bookingStepFormData.target_event &&
      bookingStepFormData.trigger_event === bookingStepFormData.target_event
    ) {
      errors.target_event = 'Target event must be different from the trigger event';
    }
    setBookingStepErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateBookingStep = async () => {
    if (!validateBookingStepForm()) return;
    setCreatingBookingStep(true);
    setBookingStepApiError('');
    try {
      const res = await createBookingStep({
        service: bookingStepFormData.service,
        label: bookingStepFormData.label.trim(),
        triggerEventId: bookingStepFormData.trigger_event,
        targetEventId: bookingStepFormData.target_event || null,
      });
      if (res?.metadata?.api_status) {
        setSuccess('Booking step created');
        resetBookingStepForm();
        // If the admin created a step for the service they already have
        // selected, refresh so it appears in the dropdown immediately.
        if (bookingStepFormData.service === EVENT_TYPE_TO_SERVICE[formData.event_type]) {
          await refreshBookingStepOptions();
        }
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setBookingStepApiError(flattenApiError(res?.msg, 'Create booking step failed'));
      }
    } catch {
      setBookingStepApiError('Create booking step failed');
    } finally {
      setCreatingBookingStep(false);
    }
  };

  // Inline "+ Add new event" — lets the admin register a brand-new trigger/
  // target event without leaving the Create Booking Step modal.
  const handleCreateEventInline = async (fieldName) => {
    const name = newEventName.trim();
    if (!name) return;
    setCreatingEvent(true);
    setBookingStepApiError('');
    try {
      const res = await createEventCatalogEntry(name, bookingStepFormData.service);
      if (res?.metadata?.api_status) {
        await loadEventCatalog(bookingStepFormData.service);
        setBookingStepFormData((prev) => ({ ...prev, [fieldName]: res.msg.id }));
        setAddingEventFor(null);
        setNewEventName('');
      } else {
        setBookingStepApiError(flattenApiError(res?.msg, 'Create event failed'));
      }
    } catch {
      setBookingStepApiError('Create event failed');
    } finally {
      setCreatingEvent(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.event_type) errors.event_type = 'Event type is required';
    if (formData.event_type === 'login_milestone') {
      if (!formData.login_number || parseInt(formData.login_number) < 1)
        errors.login_number = 'Login number must be at least 1';
    }
    if (EVENT_TYPE_TO_SERVICE[formData.event_type]) {
      if (!formData.category) errors.category = 'Please select a booking step';
    }
    if (formData.event_type === 'cart_abandonment') {
      if (!formData.service_type) errors.service_type = 'Service type is required';
    }
    if (formData.delay_minutes === '' || parseFloat(formData.delay_minutes) < 0)
      errors.delay_minutes = 'Delay must be 0 or more';
    if (!formData.watemplate) errors.watemplate = 'Please select a WhatsApp template';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateTemplate = async () => {
    if (!templateFormData.name || !templateFormData.template_name) {
      setError('Template name and EnableX template name are required');
      return;
    }
    setCreatingTemplate(true);
    setError(''); setSuccess('');
    try {
      const res = await createWaTemplate(templateFormData);
      if (res?.metadata?.api_status) {
        setSuccess('Template created');
        const templatesRes = await fetchWaTemplateOptions();
        setWaTemplates(templatesRes.msg || []);
        setTemplateFormData({ name: '', template_name: '', body: '' });
        setShowTemplateForm(false);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(typeof res?.msg === 'string' ? res.msg : 'Create template failed');
      }
    } catch {
      setError('Create template failed');
    } finally {
      setCreatingTemplate(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setFormApiError(''); setSuccess('');

    const payload = {
      event_type: formData.event_type,
      delay_minutes: parseFloat(formData.delay_minutes),
      watemplate: formData.watemplate,
    };
    if (formData.event_type === 'login_milestone') {
      payload.login_number = parseInt(formData.login_number);
      payload.service_type = null;
    } else if (EVENT_TYPE_TO_SERVICE[formData.event_type]) {
      payload.category = formData.category;
      payload.service_type = null;
      payload.login_number = null;
      payload.rate_limit_strategy = formData.rate_limit_strategy;
      if (formData.rate_limit_strategy === 'once_per_window') {
        payload.rate_limit_window_hours = parseFloat(formData.rate_limit_window_hours);
        payload.recurring_enabled = formData.recurring_enabled;
        if (formData.recurring_enabled) {
          payload.max_reminders = parseInt(formData.max_reminders, 10);
        }
      }
    } else {
      payload.service_type = formData.service_type;
      payload.login_number = null;
    }

    try {
      const res = editingId
        ? await updateEngagementRule(editingId, payload)
        : await createEngagementRule(payload);

      if (res?.metadata?.api_status) {
        if (formVariables.length > 0 && payload.watemplate) {
          await updateTemplateVariables(payload.watemplate, formVariables);
        }
        setSuccess(editingId ? 'Rule updated' : 'Rule created');
        resetForm(); loadData();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setFormApiError(flattenApiError(res?.msg, 'Operation failed'));
      }
    } catch (err) { setFormApiError('Operation failed'); }
  };

  // Reverse of EVENT_TYPE_TO_SERVICE — service key -> the event_type value
  // the create/edit form uses for that funnel.
  const SERVICE_TO_EVENT_TYPE = Object.fromEntries(
    Object.entries(EVENT_TYPE_TO_SERVICE).map(([eventType, service]) => [service, eventType])
  );

  // The REAL backend service key for a trigger event name (cep.documents.
  // booking_step.SERVICES: vc/diagnostics/loan/medicine_online/medicine_offline)
  // — NOT the same thing as SERVICE_FILTERS' UI-only 'medicine' filter bucket,
  // which collapses both medicine services into one list-page tab. Medicine
  // offline events are prefixed 'medicine_offline_'; medicine online events
  // have no distinguishing infix (e.g. 'medicine_search_page_viewed'), so
  // offline must be checked before the generic 'medicine_' fallback.
  const resolveBackendService = (triggerEventName) => {
    if (triggerEventName.startsWith('medicine_offline_')) return 'medicine_offline';
    if (triggerEventName.startsWith('medicine_')) return 'medicine_online';
    if (triggerEventName.startsWith('vc_')) return 'vc';
    if (triggerEventName.startsWith('diag_')) return 'diagnostics';
    if (triggerEventName.startsWith('loan_')) return 'loan';
    if (triggerEventName.startsWith('credit_line_')) return 'credit_line';
    return undefined;
  };

  const handleEdit = async (rule) => {
    setFormApiError('');
    const isCategoryRule = Boolean(rule.category_event_name);

    if (!isCategoryRule) {
      setFormData({
        event_type: rule.event_type,
        service_type: rule.service_type || '',
        login_number: rule.login_number ? String(rule.login_number) : '',
        category: '',
        delay_minutes: String(rule.delay_minutes),
        watemplate: rule.watemplate || '',
        rate_limit_strategy: 'once_ever',
        rate_limit_window_hours: '24', rate_limit_window_value: '24', rate_limit_window_unit: 'hours',
        recurring_enabled: false,
        max_reminders: '2',
      });
      const tmpl = waTemplates.find((t) => t.id === rule.watemplate);
      setFormVariables(Array.isArray(tmpl?.variables) ? [...tmpl.variables] : []);
      setEditingId(rule.id);
      setShowForm(true);
      return;
    }

    // Booking Step rule — infer which service it belongs to from the
    // trigger event's naming prefix, then resolve its BookingStep.id by
    // matching trigger_event_name against that service's category list
    // (rules only persist the event name, not the BookingStep.id itself).
    const service = resolveBackendService(rule.category_event_name);
    const eventType = SERVICE_TO_EVENT_TYPE[service] || 'vc_funnel_category';

    const categoriesRes = await fetchEngagementRuleCategories(service);
    const matchedCategory = categoriesRes?.metadata?.api_status
      ? (categoriesRes.msg || []).find((c) => c.trigger_event_name === rule.category_event_name)
      : null;
    if (categoriesRes?.metadata?.api_status) setCategories(categoriesRes.msg || []);

    // once_per_day no longer has its own dropdown option (it's just
    // once_per_window with a 24h window) — a legacy rule saved with it
    // still needs to land somewhere sane in the 2-option dropdown rather
    // than showing blank, so it's shown as "every 24 hours".
    const strategy = rule.rate_limit_strategy === 'once_per_day' ? 'once_per_window' : (rule.rate_limit_strategy || 'once_ever');
    const windowHoursForDisplay = rule.rate_limit_strategy === 'once_per_day' ? 24 : rule.rate_limit_window_hours;
    const window = fromWindowHours(windowHoursForDisplay);
    setFormData({
      event_type: eventType,
      service_type: '',
      login_number: '',
      category: matchedCategory?.key || '',
      delay_minutes: String(rule.delay_minutes),
      watemplate: rule.watemplate || '',
      rate_limit_strategy: strategy,
      rate_limit_window_hours: String(windowHoursForDisplay || 24),
      rate_limit_window_value: window.value,
      rate_limit_window_unit: window.unit,
      recurring_enabled: Boolean(rule.recurring_enabled),
      max_reminders: String(rule.max_reminders || 2),
    });
    const tmpl = waTemplates.find((t) => t.id === rule.watemplate);
    setFormVariables(Array.isArray(tmpl?.variables) ? [...tmpl.variables] : []);
    setEditingId(rule.id);
    setShowForm(true);
  };

  const handleToggle = async (ruleId) => {
    try {
      const res = await toggleEngagementRule(ruleId);
      if (res?.metadata?.api_status) loadData();
    } catch (err) { setError('Toggle failed'); }
  };

  const handleExpandVariables = (rule) => {
    if (expandedRow === rule.id) { setExpandedRow(null); setEditingVariables([]); return; }
    const tmpl = waTemplates.find((t) => t.id === rule.watemplate);
    setEditingVariables(Array.isArray(tmpl?.variables) ? [...tmpl.variables] : []);
    setExpandedRow(rule.id);
  };

  const getVariableLabel = (variable) => {
    const match = AVAILABLE_FIELDS.find(
      (f) => f.source === variable.source && f.field === variable.field
    );
    return match ? match.label : `${variable.source}.${variable.field}`;
  };

  const handleSaveVariables = async (rule) => {
    setSavingVariables(true); setError('');
    try {
      const res = await updateTemplateVariables(rule.watemplate, editingVariables);
      if (res?.metadata?.api_status) {
        setSuccess('Variables updated'); loadData();
        setTimeout(() => setSuccess(''), 3000);
      } else { setError(res?.msg || 'Failed to update variables'); }
    } catch (err) { setError('Failed to update variables'); }
    finally { setSavingVariables(false); }
  };

  // EngagementRule doesn't persist which service (VC/Diagnostics/Loan/
  // Medicine) a Booking Step rule came from — infer it from the trigger
  // event's naming prefix (vc_/diag_/loan_/medicine_), same convention
  // events/event_categories.py's seeded events all follow.
  const CATEGORY_SERVICE_BADGES = [
    { prefix: 'vc_', label: 'VC Booking', color: 'bg-purple-100 text-purple-700' },
    { prefix: 'diag_', label: 'Diagnostics', color: 'bg-teal-100 text-teal-700' },
    { prefix: 'loan_', label: 'Loan', color: 'bg-amber-100 text-amber-700' },
    { prefix: 'medicine_', label: 'Medicine', color: 'bg-pink-100 text-pink-700' },
    { prefix: 'credit_line_', label: 'CreditLine', color: 'bg-cyan-100 text-cyan-700' },
  ];

  const getEventBadge = (rule) => {
    if (rule.category_event_name) {
      const match = CATEGORY_SERVICE_BADGES.find((b) => rule.category_event_name.startsWith(b.prefix));
      return match || { label: 'Booking Reminder', color: 'bg-purple-100 text-purple-700' };
    }
    if (rule.event_type === 'login_milestone') return { label: 'Login', color: 'bg-blue-100 text-blue-700' };
    if (rule.event_type === 'cart_abandonment') return { label: 'Cart', color: 'bg-orange-100 text-orange-700' };
    return { label: rule.event_type, color: 'bg-gray-100 text-gray-700' };
  };

  /* Frequency column temporarily disabled — remove this comment wrapper
     (and the DotBadge import above) to restore.
  // Frequency only means anything for once_per_window rules (once_ever/
  // login/cart rules always send exactly once) — window is the repeat
  // interval, shown in the largest whole unit it cleanly divides into
  // (same helper the edit form uses to populate its own dropdown).
  const getFrequencyLabel = (rule) => {
    if (rule.rate_limit_strategy !== 'once_per_window') return 'Once only';
    const window = fromWindowHours(rule.rate_limit_window_hours);
    return `Every ${window.value} ${window.unit}`;
  };
  */

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
        <div className="w-full max-w-7xl animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-64 bg-gray-200 rounded-2xl"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/subscriber-wallet')}
              className="p-2 text-gray-500 hover:text-gray-900 hover:bg-white rounded-lg border border-transparent hover:border-gray-200 transition-all"
              aria-label="Back to home">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 leading-tight">Customer Engagement Rules</h1>
              <p className="text-sm text-gray-400">Automated WhatsApp messages for login milestones, cart abandonment &amp; booking steps</p>
            </div>
          </div>
          {!showForm && (
            <button onClick={() => { setFormApiError(''); setShowForm(true); }}
              className="self-start sm:self-auto px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 shadow-sm flex items-center gap-2 transition-all">
              <Plus className="w-4 h-4" /> Add Rule
            </button>
          )}
        </div>

        {/* Module tabs — shared with EngagementMessageLogs.jsx / ApplicationEvents.jsx
            so all three Customer Engagement pages read as one consistent module. */}
        <EngagementTabs active="rules" />

        {/* Messages */}
        {success && <div className="px-4 py-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl">{success}</div>}
        {error && <div className="px-4 py-3 text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl">{error}</div>}

        {/* Filter bar */}
        <div className="sticky top-4 z-20 bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-xs font-medium text-gray-400">Filter rules</span>
            <select value={activeTab} onChange={(e) => setActiveTab(e.target.value)}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64">
              {[
                { key: 'all', label: 'All Rules' },
                { key: 'login_milestone', label: 'Login Milestones' },
                ...SERVICE_FILTERS,
              ].map((tab) => <option key={tab.key} value={tab.key}>{tab.label}</option>)}
            </select>
          </div>
        </div>

        {/* Create/Edit Form — modal overlay. No onClick on the backdrop, so
            clicking outside does nothing (matches the Booking Step modal's
            behavior) rather than risk discarding a bigger, easier-to-lose-
            progress-on form from an accidental outside click. */}
        {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl w-full max-w-4xl shadow-xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100 sticky top-0 bg-white rounded-t-xl">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{editingId ? 'Edit Rule' : 'New Rule'}</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Configure when this reminder fires and what it sends.
              </p>
            </div>
            <button onClick={resetForm} className="text-gray-400 hover:text-gray-600 shrink-0 ml-4"><X className="w-[18px] h-[18px]" /></button>
          </div>

          <div className="px-6 py-5 space-y-7">
            {formApiError && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {formApiError}
              </div>
            )}

            {/* Section: Trigger */}
            <div>
              <h3 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Trigger</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-5">
                <div>
                  <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                    <Zap className="w-3.5 h-3.5 text-amber-500" /> Service Type <span className="text-red-500">*</span>
                  </label>
                  <select value={formData.event_type}
                    onChange={(e) => setFormData({ ...formData, event_type: e.target.value, service_type: '', login_number: '', category: '' })}
                    className={`w-full border rounded-lg px-3.5 py-3 text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.event_type ? 'border-red-500' : 'border-gray-300'}`}>
                    {EVENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>

                {/* Conditional: Login Number, Service Type, or Funnel Category */}
                {formData.event_type === 'login_milestone' ? (
                  <div>
                    <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                      <Layers className="w-3.5 h-3.5 text-purple-500" /> Login Number <span className="text-red-500">*</span>
                    </label>
                    <input type="number" min="1" value={formData.login_number}
                      onChange={(e) => setFormData({ ...formData, login_number: e.target.value })}
                      placeholder="e.g., 1 for first login"
                      className={`w-full border rounded-lg px-3.5 py-3 text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.login_number ? 'border-red-500' : 'border-gray-300'}`} />
                    {formErrors.login_number && <p className="text-red-500 text-xs mt-1">{formErrors.login_number}</p>}
                  </div>
                ) : EVENT_TYPE_TO_SERVICE[formData.event_type] ? (
                  <div className="lg:col-span-2">
                    <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                      <Layers className="w-3.5 h-3.5 text-purple-500" /> Booking Step <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <select value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className={`flex-1 min-w-0 border rounded-lg px-3.5 py-3 text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.category ? 'border-red-500' : 'border-gray-300'}`}>
                        <option value="">Select a step...</option>
                        {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                      </select>
                      <button type="button" onClick={openBookingStepForm} title="Create New Booking Step"
                        className="shrink-0 flex items-center justify-center w-11 border border-gray-300 rounded-lg text-blue-600 hover:bg-blue-50">
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    {formErrors.category && <p className="text-red-500 text-xs mt-1">{formErrors.category}</p>}
                    {formData.category && (() => {
                      const selectedCategory = categories.find((c) => c.key === formData.category);
                      if (!selectedCategory) return null;
                      return (
                        <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 flex-wrap">
                          <span className="inline-flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-full pl-2.5 pr-3 py-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                            <code className="text-gray-700 font-mono text-[11px]">{selectedCategory.trigger_event_name}</code>
                          </span>
                          <svg width="12" height="10" viewBox="0 0 20 16" fill="none" className="text-gray-300 shrink-0" aria-hidden="true">
                            <path d="M0 8H18M18 8L12 2M18 8L12 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          {selectedCategory.target_event_name ? (
                            <span className="inline-flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-full pl-2.5 pr-3 py-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                              <code className="text-gray-700 font-mono text-[11px]">{selectedCategory.target_event_name}</code>
                            </span>
                          ) : (
                            <span className="italic text-gray-400">no conversion event</span>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div>
                    <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                      <Layers className="w-3.5 h-3.5 text-purple-500" /> Service Type <span className="text-red-500">*</span>
                    </label>
                    <select value={formData.service_type}
                      onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                      className={`w-full border rounded-lg px-3.5 py-3 text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.service_type ? 'border-red-500' : 'border-gray-300'}`}>
                      <option value="">Select service...</option>
                      {SERVICE_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                    {formErrors.service_type && <p className="text-red-500 text-xs mt-1">{formErrors.service_type}</p>}
                  </div>
                )}
              </div>
            </div>

            {/* Section: Message */}
            <div className="pt-1 border-t border-gray-100">
              <h3 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3 mt-5">Message</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-5">
                <div className="lg:col-span-2">
                  <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                    <MessageCircle className="w-3.5 h-3.5 text-green-500" /> WhatsApp Template <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <select value={formData.watemplate}
                      onChange={(e) => {
                        const templateId = e.target.value;
                        setFormData({ ...formData, watemplate: templateId });
                        const tmpl = waTemplates.find((t) => t.id === templateId);
                        setFormVariables(Array.isArray(tmpl?.variables) ? [...tmpl.variables] : []);
                      }}
                      className={`flex-1 min-w-0 border rounded-lg px-3.5 py-3 text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.watemplate ? 'border-red-500' : 'border-gray-300'}`}>
                      <option value="">Select template...</option>
                      {waTemplates.filter((t) => t.channel === 'whatsapp').map((t) => (
                        <option key={t.id} value={t.id}>{t.name} ({t.template_name})</option>
                      ))}
                    </select>
                    <button type="button" onClick={() => setShowTemplateForm(true)} title="New template"
                      className="shrink-0 flex items-center justify-center w-11 border border-gray-300 rounded-lg text-blue-600 hover:bg-blue-50">
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                  {formErrors.watemplate && <p className="text-red-500 text-xs mt-1">{formErrors.watemplate}</p>}
                </div>

                <div>
                  <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                    <Clock className="w-3.5 h-3.5 text-orange-500" /> Delay after trigger
                  </label>
                  <div className="relative">
                    <input type="number" min="0" step="0.5" value={formData.delay_minutes}
                      onChange={(e) => setFormData({ ...formData, delay_minutes: e.target.value })}
                      className={`w-full border rounded-lg pl-3.5 pr-16 py-3 text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.delay_minutes ? 'border-red-500' : 'border-gray-300'}`} />
                    <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">minutes</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Section: Frequency — only meaningful for funnel-category rules.
                Temporarily disabled (Remind Again + auto-repeat toggle) for
                create/edit — flip this back to just
                `EVENT_TYPE_TO_SERVICE[formData.event_type]` to re-enable. */}
            {false && EVENT_TYPE_TO_SERVICE[formData.event_type] && (
              <div className="pt-1 border-t border-gray-100">
                <h3 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3 mt-5">Frequency</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-5">
                  <div>
                    <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                      <Bell className="w-3.5 h-3.5 text-rose-500" /> Remind Again?
                    </label>
                    <select value={formData.rate_limit_strategy}
                      onChange={(e) => setFormData({ ...formData, rate_limit_strategy: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3.5 py-3 text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                      {RATE_LIMIT_STRATEGIES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                  {formData.rate_limit_strategy === 'once_per_window' && (
                    <div>
                      <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                        <Hourglass className="w-3.5 h-3.5 text-cyan-600" /> Remind again after
                      </label>
                      <div className="flex gap-2">
                        <input type="number" min="1" value={formData.rate_limit_window_value}
                          onChange={(e) => {
                            const rate_limit_window_value = e.target.value;
                            setFormData((prev) => ({
                              ...prev,
                              rate_limit_window_value,
                              rate_limit_window_hours: String(toWindowHours(rate_limit_window_value, prev.rate_limit_window_unit)),
                            }));
                          }}
                          className="w-1/2 border border-gray-300 rounded-lg px-3.5 py-3 text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                        <select value={formData.rate_limit_window_unit}
                          onChange={(e) => {
                            const rate_limit_window_unit = e.target.value;
                            setFormData((prev) => ({
                              ...prev,
                              rate_limit_window_unit,
                              rate_limit_window_hours: String(toWindowHours(prev.rate_limit_window_value, rate_limit_window_unit)),
                            }));
                          }}
                          className="w-1/2 border border-gray-300 rounded-lg px-3.5 py-3 text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                          {WINDOW_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                {formData.rate_limit_strategy === 'once_per_window' && (
                  <div className={`mt-5 flex items-start justify-between gap-4 rounded-xl px-4 py-3.5 border transition-colors ${formData.recurring_enabled ? 'bg-gradient-to-r from-indigo-50 to-blue-50 border-indigo-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <span className={`flex items-center justify-center w-8 h-8 rounded-lg shrink-0 ${formData.recurring_enabled ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-200 text-gray-400'}`}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M17 2.1l4 4-4 4M3 12.9v-1a4 4 0 014-4h14M7 21.9l-4-4 4-4M21 11.1v1a4 4 0 01-4 4H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                      <div className="min-w-0 pt-0.5">
                        <p className="text-sm font-medium text-gray-800">Auto-repeat without a new event</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Keeps sending on the interval above until the subscriber converts or the cap is reached.
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <button type="button" role="switch" aria-checked={formData.recurring_enabled}
                        onClick={() => setFormData({ ...formData, recurring_enabled: !formData.recurring_enabled })}
                        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${formData.recurring_enabled ? 'bg-indigo-600' : 'bg-gray-300'}`}>
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.recurring_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                      {formData.recurring_enabled && (
                        <div className="flex items-center gap-1.5 pt-2 mt-1 border-t border-indigo-200 w-full">
                          <label className="text-xs text-indigo-700 font-medium whitespace-nowrap">Max sends</label>
                          <input type="number" min="1" max="20" value={formData.max_reminders}
                            onChange={(e) => setFormData({ ...formData, max_reminders: e.target.value })}
                            className="w-16 border border-indigo-300 rounded-lg px-2 py-1.5 text-sm text-center bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Template preview */}
            {formData.watemplate && (() => {
              const selectedTemplate = waTemplates.find((t) => t.id === formData.watemplate);
              return selectedTemplate?.body ? (
                <div className="p-2.5 bg-green-50 border border-green-200 rounded-md text-xs text-green-800">
                  <span className="font-semibold">Preview: </span>{selectedTemplate.body}
                </div>
              ) : null;
            })()}

            {/* Template variables */}
            {formData.watemplate && (
              <div className="p-3.5 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-2.5">
                  <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Template Variables</h3>
                  <button onClick={() => setFormVariables([...formVariables, { source: '', field: '' }])}
                    className="flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2.5 py-1 rounded hover:bg-blue-200 shrink-0 ml-3">
                    <Plus className="w-2.5 h-2.5" /> Add Variable
                  </button>
                </div>
                {formVariables.length === 0 ? (
                  <p className="text-xs text-gray-500 italic">No variables. Click "Add Variable" if the template has dynamic placeholders.</p>
                ) : (
                  <div className="space-y-2">
                    {formVariables.map((variable, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <span className="text-xs text-gray-400 w-10 shrink-0 font-mono">{`{{${idx + 1}}}`}</span>
                        <select
                          value={AVAILABLE_FIELDS.find((f) => f.source === variable.source && f.field === variable.field)?.label || ''}
                          onChange={(e) => {
                            const field = AVAILABLE_FIELDS.find((f) => f.label === e.target.value);
                            if (!field) return;
                            const updated = [...formVariables];
                            updated[idx] = { source: field.source, field: field.field };
                            setFormVariables(updated);
                          }}
                          className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white focus:ring-2 focus:ring-blue-500">
                          <option value="">Select field...</option>
                          {AVAILABLE_FIELDS.map((f) => <option key={f.label} value={f.label}>{f.label}</option>)}
                        </select>
                        <button onClick={() => setFormVariables(formVariables.filter((_, i) => i !== idx))}
                          className="text-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* New template modal */}
          {showTemplateForm && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">New WhatsApp Template</h3>
                  <button onClick={() => setShowTemplateForm(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                    <input type="text" value={templateFormData.name}
                      onChange={(e) => setTemplateFormData({ ...templateFormData, name: e.target.value })}
                      placeholder="e.g., Diagnostics Cart Reminder"
                      className="w-full border border-gray-300 rounded-md px-3.5 py-3 text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">EnableX Template Name</label>
                    <input type="text" value={templateFormData.template_name}
                      onChange={(e) => setTemplateFormData({ ...templateFormData, template_name: e.target.value })}
                      placeholder="Exact name approved in EnableX"
                      className="w-full border border-gray-300 rounded-md px-3.5 py-3 text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Body Preview (optional)</label>
                    <textarea value={templateFormData.body}
                      onChange={(e) => setTemplateFormData({ ...templateFormData, body: e.target.value })}
                      placeholder="Hi {{1}}, you left items in your cart!"
                      rows={2}
                      className="w-full border border-gray-300 rounded-md px-3.5 py-3 text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                <div className="mt-4 flex gap-3">
                  <button onClick={handleCreateTemplate} disabled={creatingTemplate}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50">
                    {creatingTemplate ? 'Creating...' : 'Create'}
                  </button>
                  <button onClick={() => setShowTemplateForm(false)}
                    className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 text-sm font-medium">Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* New booking step modal */}
          {showBookingStepForm && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl w-full max-w-xl shadow-xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Create New Booking Step</h3>
                    <p className="text-sm text-gray-500 mt-0.5">
                      Define a funnel stage: when it starts, and what "completed" looks like.
                    </p>
                  </div>
                  <button onClick={resetBookingStepForm} className="text-gray-400 hover:text-gray-600 shrink-0 ml-4"><X className="w-[18px] h-[18px]" /></button>
                </div>

                <div className="px-6 py-5 space-y-6">
                  {bookingStepApiError && (
                    <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                      {bookingStepApiError}
                    </div>
                  )}

                  {/* Section 1 — Service + Journey Name */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white text-[11px] font-semibold shrink-0">1</span>
                      <h4 className="text-sm font-semibold text-gray-800">Which flow is this for?</h4>
                    </div>
                    <div className="grid grid-cols-2 gap-4 pl-7">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Service <span className="text-red-500">*</span></label>
                        <select value={bookingStepFormData.service}
                          onChange={(e) => handleBookingStepServiceChange(e.target.value)}
                          className={`w-full border rounded-md px-3.5 py-3 text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${bookingStepErrors.service ? 'border-red-500' : 'border-gray-300'}`}>
                          <option value="">Select service...</option>
                          {BOOKING_STEP_SERVICES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                        {bookingStepErrors.service && <p className="text-red-500 text-xs mt-1">{bookingStepErrors.service}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Journey Name <span className="text-red-500">*</span></label>
                        <input type="text" value={bookingStepFormData.label}
                          onChange={(e) => setBookingStepFormData({ ...bookingStepFormData, label: e.target.value })}
                          placeholder="e.g., Stuck at Cart"
                          className={`w-full border rounded-md px-3.5 py-3 text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${bookingStepErrors.label ? 'border-red-500' : 'border-gray-300'}`} />
                        {bookingStepErrors.label && <p className="text-red-500 text-xs mt-1">{bookingStepErrors.label}</p>}
                      </div>
                    </div>
                  </div>

                  {/* Section 2 — Trigger → Target, with a visual flow connector */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`flex items-center justify-center w-5 h-5 rounded-full text-[11px] font-semibold shrink-0 ${bookingStepFormData.service ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>2</span>
                      <h4 className="text-sm font-semibold text-gray-800">What starts it, and what completes it?</h4>
                    </div>

                    <div className="pl-7 relative">
                      {!bookingStepFormData.service && (
                        <p className="text-xs text-gray-400 italic mb-3">Pick a service above to choose from its events.</p>
                      )}

                      <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-3">
                        {/* Trigger Event — required; this is the event
                            EngagementRule matches an inbound Event against. */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Trigger Event <span className="text-red-500">*</span>
                          </label>
                          <SearchableEventSelect
                            events={eventCatalog}
                            value={bookingStepFormData.trigger_event}
                            onChange={(id) => setBookingStepFormData((prev) => ({ ...prev, trigger_event: id }))}
                            disabled={!bookingStepFormData.service}
                            placeholder={bookingStepFormData.service ? 'Search events...' : 'Select a service first'} />
                          {bookingStepErrors.trigger_event && <p className="text-red-500 text-xs mt-1">{bookingStepErrors.trigger_event}</p>}
                          {addingEventFor === 'trigger_event' ? (
                            <div className="mt-2 p-2.5 bg-blue-50 border border-blue-100 rounded-md">
                              <div className="flex items-center justify-between mb-1.5">
                                <span className="text-xs font-medium text-blue-800">New Trigger Event</span>
                                <button type="button" onClick={() => { setAddingEventFor(null); setNewEventName(''); }}
                                  className="text-blue-300 hover:text-blue-600"><X className="w-3 h-3" /></button>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <input type="text" value={newEventName} autoFocus
                                  onChange={(e) => setNewEventName(e.target.value)}
                                  onKeyDown={(e) => e.key === 'Enter' && handleCreateEventInline('trigger_event')}
                                  placeholder="e.g., diag_cart_viewed"
                                  className="flex-1 min-w-0 border border-blue-200 rounded-md px-2 py-1.5 text-xs bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                                <button type="button" onClick={() => handleCreateEventInline('trigger_event')} disabled={creatingEvent || !newEventName.trim()}
                                  className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 disabled:opacity-50 shrink-0 font-medium">
                                  {creatingEvent ? '...' : 'Add'}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button type="button" disabled={!bookingStepFormData.service}
                              onClick={() => { setAddingEventFor('trigger_event'); setNewEventName(''); }}
                              className="mt-1.5 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed">
                              <Plus className="w-2.5 h-2.5" /> New event
                            </button>
                          )}
                        </div>

                        {/* Flow connector */}
                        <div className="flex items-center justify-center pt-7 text-gray-300">
                          <svg width="20" height="16" viewBox="0 0 20 16" fill="none" aria-hidden="true">
                            <path d="M0 8H18M18 8L12 2M18 8L12 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>

                        {/* Target Event — optional; a step without one has no
                            conversion event to attribute against, but is
                            still a valid category to hand-pick for a rule. */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Target Event <span className="text-gray-400 font-normal">(optional)</span>
                          </label>
                          <SearchableEventSelect
                            events={eventCatalog}
                            value={bookingStepFormData.target_event}
                            onChange={(id) => setBookingStepFormData((prev) => ({ ...prev, target_event: id }))}
                            disabled={!bookingStepFormData.service}
                            placeholder={bookingStepFormData.service ? 'Search events...' : 'Select a service first'} />
                          {bookingStepErrors.target_event && <p className="text-red-500 text-xs mt-1">{bookingStepErrors.target_event}</p>}
                          {!bookingStepFormData.target_event && (
                            <p className="text-xs text-gray-400 mt-1">Without one, conversions won't be tracked for this step.</p>
                          )}
                          {addingEventFor === 'target_event' ? (
                            <div className="mt-2 p-2.5 bg-blue-50 border border-blue-100 rounded-md">
                              <div className="flex items-center justify-between mb-1.5">
                                <span className="text-xs font-medium text-blue-800">New Target Event</span>
                                <button type="button" onClick={() => { setAddingEventFor(null); setNewEventName(''); }}
                                  className="text-blue-300 hover:text-blue-600"><X className="w-3 h-3" /></button>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <input type="text" value={newEventName} autoFocus
                                  onChange={(e) => setNewEventName(e.target.value)}
                                  onKeyDown={(e) => e.key === 'Enter' && handleCreateEventInline('target_event')}
                                  placeholder="e.g., diag_payment_success"
                                  className="flex-1 min-w-0 border border-blue-200 rounded-md px-2 py-1.5 text-xs bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                                <button type="button" onClick={() => handleCreateEventInline('target_event')} disabled={creatingEvent || !newEventName.trim()}
                                  className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 disabled:opacity-50 shrink-0 font-medium">
                                  {creatingEvent ? '...' : 'Add'}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button type="button" disabled={!bookingStepFormData.service}
                              onClick={() => { setAddingEventFor('target_event'); setNewEventName(''); }}
                              className="mt-1.5 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed">
                              <Plus className="w-2.5 h-2.5" /> New event
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-xl">
                  <button onClick={resetBookingStepForm}
                    className="bg-white border border-gray-300 text-gray-700 px-5 py-2 rounded-lg hover:bg-gray-100 text-sm font-medium">Cancel</button>
                  <button onClick={handleCreateBookingStep} disabled={creatingBookingStep}
                    className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50">
                    {creatingBookingStep ? 'Creating...' : 'Create Booking Step'}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-xl">
            <button onClick={resetForm} className="bg-white border border-gray-300 text-gray-700 px-5 py-2 rounded-lg hover:bg-gray-100 text-sm font-medium">Cancel</button>
            <button onClick={handleSubmit} className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium">
              {editingId ? 'Update Rule' : 'Create Rule'}
            </button>
          </div>
        </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {['Type', 'Journey', 'Trigger', 'WhatsApp Template', 'Delay', /* 'Frequency', */ 'Actions'].map((h, i) => (
                  <th key={i} className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap ${i === 0 ? 'text-left' : 'text-center'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {visibleRules.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center gap-3 text-gray-400">
                      <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center">
                        <ListChecks className="w-6 h-6 text-gray-300" />
                      </div>
                      <p className="text-sm font-medium text-gray-500">No rules configured. Click "Add Rule" to get started.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                visibleRules.map((r) => {
                  const badge = getEventBadge(r);
                  return (
                    <React.Fragment key={r.id}>
                      <tr className="hover:bg-blue-50/40 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-left">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-md ${badge.color}`}>{badge.label}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <p className="text-sm font-medium text-gray-900">{r.journey_name || '—'}</p>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-center">
                          {r.category_event_name ? (
                            <div>
                              <div className="text-xs font-medium">{r.category_event_name}</div>
                              {r.conversion_event && (
                                <div className="text-xs text-gray-400">→ {r.conversion_event}</div>
                              )}
                            </div>
                          ) : r.event_type === 'login_milestone' ? (
                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-bold text-sm">{r.login_number}</span>
                          ) : (
                            <span className="text-xs font-medium capitalize">{(r.service_type || '').replace('_', ' ')}</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="text-sm font-medium text-gray-900">{r.watemplate_detail?.name || 'No template'}</div>
                          <div className="text-xs text-gray-500">{r.watemplate_detail?.template_name || ''}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700 text-center whitespace-nowrap">
                          {r.delay_minutes === 0 ? 'Now' : `${r.delay_minutes}m`}
                        </td>
                        {/* Frequency column temporarily disabled — remove this
                            comment wrapper to restore.
                        <td className="px-6 py-4 text-center whitespace-nowrap">
                          <p className="text-sm text-gray-700">{getFrequencyLabel(r)}</p>
                          {r.rate_limit_strategy === 'once_per_window' && (
                            r.recurring_enabled ? (
                              <div className="mt-1">
                                <DotBadge label={`Auto-repeat · max ${r.max_reminders ?? '—'}`} colorClass="bg-indigo-50 text-indigo-700 ring-indigo-600/20" dotClass="bg-indigo-500" />
                              </div>
                            ) : (
                              <p className="text-xs text-gray-400 mt-0.5">Auto-repeat off</p>
                            )
                          )}
                        </td>
                        */}
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center gap-3">
                            <button onClick={() => handleEdit(r)}
                              className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 hover:text-blue-700 transition-colors" title="Edit">
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button type="button" role="switch" aria-checked={r.active}
                              onClick={() => handleToggle(r.id)}
                              title={r.active ? 'Deactivate' : 'Activate'}
                              className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${r.active ? 'bg-emerald-600' : 'bg-gray-300'}`}>
                              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${r.active ? 'translate-x-[1.125rem]' : 'translate-x-1'}`} />
                            </button>
                            <button onClick={() => handleExpandVariables(r)}
                              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors" title="Variables">
                              {expandedRow === r.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expandedRow === r.id && (
                        <tr>
                          <td colSpan="6" className="px-6 py-4 bg-gray-50">
                            <div className="max-w-2xl mx-auto">
                              {/* Template preview */}
                              {(() => {
                                const tmpl = waTemplates.find((t) => t.id === r.watemplate);
                                return (
                                  <div className="mb-4 p-3 bg-white border border-gray-200 rounded-lg">
                                    <div className="text-xs text-gray-400 mb-1 font-medium uppercase tracking-wider">Template: {r.watemplate_detail?.name}</div>
                                    <div className="text-xs text-gray-500 mb-2">Meta name: <code className="bg-gray-100 px-1 rounded">{r.watemplate_detail?.template_name}</code></div>
                                    {tmpl?.body ? (
                                      <div className="text-sm text-gray-800 bg-green-50 border border-green-100 rounded p-2 font-mono whitespace-pre-wrap">{tmpl.body}</div>
                                    ) : (
                                      <div className="text-xs text-gray-400 italic">No body stored. Managed in EnableX dashboard.</div>
                                    )}
                                  </div>
                                );
                              })()}

                              <div className="flex items-center justify-between mb-3">
                                <h3 className="text-sm font-semibold text-gray-700">Template Variables</h3>
                                <button onClick={() => setEditingVariables([...editingVariables, { source: '', field: '' }])}
                                  className="flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded hover:bg-blue-200">
                                  <Plus className="w-2.5 h-2.5" /> Add Variable
                                </button>
                              </div>
                              {editingVariables.length === 0 ? (
                                <p className="text-sm text-gray-500 italic">No variables configured.</p>
                              ) : (
                                <div className="space-y-2">
                                  {editingVariables.map((variable, idx) => (
                                    <div key={idx} className="flex items-center gap-3">
                                      <span className="text-xs text-gray-400 w-12 shrink-0">{`{{${idx + 1}}}`}</span>
                                      <select value={getVariableLabel(variable)}
                                        onChange={(e) => {
                                          const field = AVAILABLE_FIELDS.find((f) => f.label === e.target.value);
                                          if (!field) return;
                                          const updated = [...editingVariables];
                                          updated[idx] = { source: field.source, field: field.field };
                                          setEditingVariables(updated);
                                        }}
                                        className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white">
                                        <option value="">Select field...</option>
                                        {AVAILABLE_FIELDS.map((f) => <option key={f.label} value={f.label}>{f.label}</option>)}
                                      </select>
                                      <button onClick={() => setEditingVariables(editingVariables.filter((_, i) => i !== idx))}
                                        className="text-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <div className="mt-3 flex gap-2">
                                <button onClick={() => handleSaveVariables(r)} disabled={savingVariables}
                                  className="bg-blue-600 text-white px-4 py-1.5 rounded text-xs font-medium hover:bg-blue-700 disabled:opacity-50">
                                  {savingVariables ? 'Saving...' : 'Save Variables'}
                                </button>
                                <button onClick={() => setExpandedRow(null)}
                                  className="bg-gray-100 text-gray-600 px-4 py-1.5 rounded text-xs font-medium hover:bg-gray-200">Close</button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info Box — collapsed by default so it doesn't compete with the table
          for attention; expands on click for admins who want the reference. */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl overflow-hidden">
        <button type="button" onClick={() => setShowHowItWorks((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 text-left">
          <h3 className="text-sm font-semibold text-blue-800">How it works</h3>
          {showHowItWorks ? <ChevronUp className="w-4 h-4 text-blue-600 shrink-0" /> : <ChevronDown className="w-4 h-4 text-blue-600 shrink-0" />}
        </button>
        {showHowItWorks && (
          <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside px-4 pb-4">
            <li><strong>Login Milestones:</strong> WhatsApp sent when a subscriber's login count reaches the configured number.</li>
            <li><strong>Cart Abandonment:</strong> WhatsApp sent if a subscriber adds items but doesn't complete booking within the delay.</li>
            <li><strong>Booking Steps:</strong> WhatsApp sent when a subscriber reaches a funnel stage (e.g. Slot Selection) without completing its Journey's target event within the delay.</li>
            <li><strong>Remind Again:</strong> a Booking Step rule can re-send on a repeat interval instead of sending only once — see "Remind Again?" in the rule form.</li>
            <li><strong>Auto-repeat reminders:</strong> when enabled, reminders keep firing automatically on that interval — no new trigger event needed — up to the configured max, and stop immediately once the subscriber converts.</li>
            <li>Dynamic template variables (subscriber name, plan, etc.) are configured per rule.</li>
          </ul>
        )}
      </div>
      </div>
    </div>
  );
};

export default EngagementRuleConfig;
